import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { useInvalidatePinnedNotifications } from '@/hooks/notifications';
import { patchUserProfileMutationFn } from '@/queries/users';
import { useSelector } from '@/redux/helper';
import { updateUserProperty } from '@/redux/slices/user/actions';

/**
 * Get the persona the flow asked for onto the account, if the account has none.
 *
 * Most of the time nothing happens here: the answer is given on the opening step and travels with
 * the registration, so by the time this could act the account already has it. This is for the ways
 * round that do not go through the registration — a social sign-up, and the confirmation card,
 * which asks a visitor who is already signed in.
 *
 * Keyed on having an account rather than on the answer being given, because the opening step asks a
 * signed-out visitor: the machine carries the answer until there is somewhere to put it.
 *
 * It fills an empty field and never rewrites a full one. The opening step asks before anyone knows
 * which account is coming, so a returning visitor who answers it and then takes the "I already have
 * an account" branch would otherwise have this overwrite the answer their account already held —
 * silently editing a profile field they never opened.
 *
 * Idempotent through redux — a successful write lands there and this stops matching.
 */
const usePersistPersona = () => {
  const dispatch = useDispatch();
  const invalidatePinnedNotifications = useInvalidatePinnedNotifications();
  const logged = useSelector((state) => state.user.logged);
  const savedPersona = useSelector((state) => state.user.persona);
  const chosenPersona = useSelector((state) => state.modals.speedyFlow.machine?.persona);

  const { mutate: savePersona } = useMutation({
    mutationFn: patchUserProfileMutationFn,
    onSuccess: (_data, variables) => {
      if (variables.persona) {
        dispatch(updateUserProperty({ persona: variables.persona }));
      }
      // Declaring the educator persona can mint a pinned notification server-side, and the bell only
      // learns about it by asking.
      invalidatePinnedNotifications();
    },
    onError: (err) => console.error('Could not save persona', err),
  });

  useEffect(() => {
    if (!chosenPersona || !logged || savedPersona) {
      return;
    }
    savePersona({ persona: chosenPersona });
  }, [chosenPersona, logged, savedPersona, savePersona]);
};

export default usePersistPersona;
